import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Camera, Music, Utensils, Fish, Waves } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const Culture = () => {
  const landmarks = [
    {
      name: "Seven Mile Beach",
      description:
        "World-famous pristine white sand beach where Bobo loves to swim and Teedee loves to play.",
      icon: Waves,
      funFact: "Despite its name, Seven Mile Beach is actually 6.2 miles long!",
    },
    {
      name: "Stingray City",
      description:
        "Magical underwater world where friendly stingrays swim alongside visitors.",
      icon: Fish,
      funFact:
        "The stingrays here are so friendly, they love to be petted and fed!",
    },
    {
      name: "George Town",
      description:
        "The capital city filled with colorful buildings and rich Caymanian history.",
      icon: MapPin,
      funFact:
        "George Town is home to the famous Cayman Islands National Museum!",
    },
    {
      name: "Pedro St. James",
      description:
        "Historic great house known as the 'Birthplace of Democracy in the Cayman Islands.'",
      icon: Camera,
      funFact:
        "This historic site is over 200 years old and offers amazing views!",
    },
  ];

  const traditions = [
    {
      title: "Cayman Islands National Song",
      description:
        "Learn about 'Beloved Isle Cayman' and why it's special to every Caymanian heart.",
      icon: Music,
      color: "teedee-blue",
    },
    {
      title: "Traditional Cayman Cuisine",
      description:
        "Discover local favorites like Stew conch, Cassava Cake, and the famous Cayman-style beef.",
      icon: Utensils,
      color: "golden-orange",
    },
    {
      title: "Cayman Islands Pirates Week",
      description:
        "The islands' most famous festival celebrating heritage with parades, music, and fun!",
      icon: Camera,
      color: "brand-purple",
    },
  ];

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-sky-blue/20 via-white to-bobo-green/20">
        {/* Header */}
        <section className="w-full bg-gradient-to-r from-teedee-blue to-sky-blue text-white">
          <div className="w-full py-20 lg:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h1 className="text-5xl lg:text-6xl font-bold mb-8">
                Explore Cayman Culture
              </h1>
              <p className="text-xl lg:text-2xl max-w-4xl mx-auto opacity-90 leading-relaxed">
                Discover the beautiful traditions, landmarks, and heritage of
                the Cayman Islands through the eyes of Bobo & Teedee®!
              </p>
            </div>
          </div>
        </section>

        {/* Interactive Map Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-teedee-blue mb-4">
                Famous Cayman Islands Landmarks
              </h2>
              <p className="text-xl text-gray-600">
                Join Bobo & Teedee® as they explore these amazing places!
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              {/* Map Placeholder */}
              <div className="relative">
                <img
                  src="/img/cayman-aerial.png?w=600&h=400&fit=crop"
                  alt="Beautiful aerial view of the Cayman Islands"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teedee-blue/20 to-transparent rounded-2xl"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-2xl font-bold mb-2">
                    The Cayman Islands
                  </h3>
                  <p className="text-lg opacity-90">
                    Three beautiful islands in the Caribbean Sea
                  </p>
                </div>
              </div>

              {/* Landmarks Info */}
              <div className="space-y-6">
                <div className="text-center lg:text-left">
                  <h3 className="text-3xl font-bold text-teedee-blue mb-4">
                    Did You Know?
                  </h3>
                  <p className="text-lg text-gray-600 mb-6">
                    The Cayman Islands consist of three islands: Grand Cayman,
                    Cayman Brac, and Little Cayman. Each island has its own
                    unique personality and attractions!
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-teedee-blue/10 p-4 rounded-xl">
                    <h4 className="font-bold text-teedee-blue mb-2">
                      Grand Cayman
                    </h4>
                    <p className="text-sm text-gray-600">
                      The largest island, home to George Town and Seven Mile
                      Beach
                    </p>
                  </div>
                  <div className="bg-bobo-green/10 p-4 rounded-xl">
                    <h4 className="font-bold text-bobo-green mb-2">
                      Cayman Brac
                    </h4>
                    <p className="text-sm text-gray-600">
                      Known for its dramatic cliffs and caves
                    </p>
                  </div>
                  <div className="bg-brand-purple/10 p-4 rounded-xl">
                    <h4 className="font-bold text-brand-purple mb-2">
                      Little Cayman
                    </h4>
                    <p className="text-sm text-gray-600">
                      The smallest island, perfect for nature lovers
                    </p>
                  </div>
                  <div className="bg-golden-orange/10 p-4 rounded-xl">
                    <h4 className="font-bold text-golden-orange mb-2">
                      Total Population
                    </h4>
                    <p className="text-sm text-gray-600">
                      About 65,000 friendly Caymanians!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Landmarks Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {landmarks.map((landmark, index) => (
                <Card
                  key={index}
                  className="overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-white/80 backdrop-blur-sm border-2 border-sky-blue/20"
                >
                  <CardContent className="p-8">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-teedee-blue/20 rounded-full flex items-center justify-center mr-4">
                        <landmark.icon className="h-6 w-6 text-teedee-blue" />
                      </div>
                      <h3 className="text-xl font-bold text-teedee-blue">
                        {landmark.name}
                      </h3>
                    </div>
                    <p className="text-gray-600 mb-4">{landmark.description}</p>
                    <div className="bg-bobo-green/10 p-3 rounded-lg">
                      <p className="text-sm font-semibold text-bobo-green">
                        Fun Fact:
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {landmark.funFact}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Traditions Section */}
        <section className="py-16 bg-white/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-teedee-blue mb-4">
                Cayman Islands Traditions
              </h2>
              <p className="text-xl text-gray-600">
                Experience the rich cultural heritage that makes the Cayman
                Islands special!
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {traditions.map((tradition, index) => (
                <Card
                  key={index}
                  className={`text-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 bg-white/80 backdrop-blur-sm border-2 border-${tradition.color}/20`}
                >
                  <CardContent className="p-8">
                    <div
                      className={`w-16 h-16 bg-${tradition.color}/20 rounded-full flex items-center justify-center mb-6 mx-auto`}
                    >
                      <tradition.icon
                        className={`h-8 w-8 text-${tradition.color}`}
                      />
                    </div>
                    <h3
                      className={`text-xl font-bold text-${tradition.color} mb-4`}
                    >
                      {tradition.title}
                    </h3>
                    <p className="text-gray-600">{tradition.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Cultural Values */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-teedee-blue mb-4">
                Caymanian Values
              </h2>
              <p className="text-xl text-gray-600">
                The heart of Cayman culture lies in these important values that
                Bobo & Teedee® celebrate.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <img
                  src="https://images.unsplash.com/photo-1509062522246-3755977927d7?w=600&h=400&fit=crop"
                  alt="Happy Caymanian families enjoying time together"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
              </div>

              <div className="space-y-6">
                <div className="bg-teedee-blue/10 p-6 rounded-2xl">
                  <h3 className="text-xl font-bold text-teedee-blue mb-3">
                    Family First
                  </h3>
                  <p className="text-gray-600">
                    Family is the foundation of Caymanian society. Extended
                    families often live close together and support each other
                    through everything.
                  </p>
                </div>

                <div className="bg-bobo-green/10 p-6 rounded-2xl">
                  <h3 className="text-xl font-bold text-bobo-green mb-3">
                    Respect for Elders
                  </h3>
                  <p className="text-gray-600">
                    Older community members are highly respected for their
                    wisdom and experience. Their stories keep Cayman traditions
                    alive.
                  </p>
                </div>

                <div className="bg-brand-purple/10 p-6 rounded-2xl">
                  <h3 className="text-xl font-bold text-brand-purple mb-3">
                    Community Spirit
                  </h3>
                  <p className="text-gray-600">
                    Caymanians believe in helping their neighbors and working
                    together to make their community stronger and more caring.
                  </p>
                </div>

                <div className="bg-golden-orange/10 p-6 rounded-2xl">
                  <h3 className="text-xl font-bold text-golden-orange mb-3">
                    Love of Nature
                  </h3>
                  <p className="text-gray-600">
                    Living surrounded by beautiful oceans and marine life,
                    Caymanians have a deep respect for nature and the
                    environment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 bg-gradient-to-r from-teedee-blue via-bobo-green to-brand-purple text-white">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-6">
              Experience Cayman Culture with Bobo & Teedee®!
            </h2>
            <p className="text-lg mb-8 opacity-90">
              Ready to explore more of the beautiful Cayman Islands culture?
              Watch our episodes to see these amazing places and traditions come
              to life!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/episodes"
                className="bg-white text-teedee-blue hover:bg-white/90 px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 inline-block"
              >
                Watch Episodes
              </a>
              <a
                href="/activities"
                className="border-2 border-white text-white hover:bg-white hover:text-teedee-blue px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 inline-block"
              >
                Try Activities
              </a>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Culture;
